import { useState } from "react";
import type {
  AccountingDay,
  AccountingLocation,
  AccountingTargets,
  AccountingWorkDay,
  AccountingYear,
} from "@dentacore/shared-types";
import { daysInMonth, isoDay } from "@dentacore/shared-types";

import {
  BilledLegend,
  HeadlineTiles,
  OverheadLegend,
  OverheadRows,
  OwedByAge,
} from "./billing-cards";
import {
  CategoryBars,
  FigureTiles,
  signClass,
  sumMonths,
  UnpricedNote,
  WorkLegend,
} from "./figures";
import { money, monthName, monthShort, parseDay, plural, weekdayShort } from "./format";
import { FlowChart } from "./flow-chart";
import { buildFlow } from "./flow-layout";
import { ProfitChart } from "./profit-chart";
import type { ChartBar } from "./profit-chart";
import { useModules } from "../shell/modules-context";
import styles from "./accounting.module.css";
import { PagedRows } from "../ui/paging";

/**
 * Monthly — one month, day by day (STORY-180b, ER-D1, ER-D8).
 *
 * The three figures for the month; **Expected and realized by day**, a day
 * still ahead showing only what is booked into it; the cash profit by day, as
 * the old Overview's day chart had it; and the month by procedure category.
 *
 * A day's cash profit is money in less materials received that day — **no
 * salary and no expense** (AC-D5, AC-D8) — so the days add up to more than the
 * month's profit, and the note under that chart shows both figures. Expected
 * and Realized have no such gap: a booking belongs to its day whole, so the
 * days add up to the month exactly (ER-D6).
 *
 * The month is the page's, so Yearly can open one here; ‹ and › cross into
 * the neighbouring year, as far as the years the page offers.
 */
export function MonthlyTab({
  data,
  year,
  month,
  canGoBack,
  canGoForward,
  onMonth,
  currency,
  selectionLabel,
  targets,
  locations,
}: {
  /** Null while the year is loading. */
  data: AccountingYear | null;
  year: number;
  month: number;
  canGoBack: boolean;
  canGoForward: boolean;
  /** 0 is December of the year before, 13 January of the year after. */
  onMonth: (month: number) => void;
  currency: string;
  selectionLabel: string;
  targets: AccountingTargets | null;
  /** The locations selected — the left side of the money flow (FL-D2). */
  locations: readonly AccountingLocation[];
}): React.JSX.Element {
  // Materials are Supply's: without it Accounting shows none (QF-D4).
  const hasSupply = useModules().has("SUPPLY");
  const [day, setDay] = useState<string | null>(null);
  const [showWork, setShowWork] = useState(false);
  const [showDays, setShowDays] = useState(false);

  const title = `${monthName(month)} ${String(year)}`;
  const move = (next: number): void => {
    setDay(null);
    onMonth(next);
  };

  const controls = (
    <div className={styles.controls}>
      <div className={styles.monthNav}>
        <button
          type="button"
          className={`${styles.ghost} ${styles.small}`}
          onClick={() => move(month - 1)}
          disabled={!canGoBack}
          aria-label="Previous month"
          data-testid="accounting-month-prev"
        >
          ‹
        </button>
        <span className={styles.monthNavLabel} data-testid="accounting-month-label">
          {title}
        </span>
        <button
          type="button"
          className={`${styles.ghost} ${styles.small}`}
          onClick={() => move(month + 1)}
          disabled={!canGoForward}
          aria-label="Next month"
          data-testid="accounting-month-next"
        >
          ›
        </button>
      </div>
      <p className={styles.note}>Showing {selectionLabel}.</p>
    </div>
  );

  const monthRow = data?.months[month - 1];
  if (data === null || monthRow === undefined) {
    return (
      <>
        {controls}
        <div className={styles.card}>
          <p className={styles.blank}>Loading…</p>
        </div>
      </>
    );
  }

  const total = sumMonths([monthRow]);
  const flow = buildFlow({
    locations,
    byLocation: monthRow.byLocation,
    moneyIn: total.moneyIn,
    overhead: total.overhead,
    profit: total.profit,
  });
  const workByDate = new Map<string, AccountingWorkDay>(data.work.map((row) => [row.date, row]));
  const cashByDate = new Map<string, AccountingDay>(data.days.map((row) => [row.date, row]));

  const days = Array.from({ length: daysInMonth(year, month) }, (_, i) => {
    const date = isoDay(year, month, i + 1);
    const future = date > data.today;
    const work = workByDate.get(date) ?? null;
    // A day up to today with no row is a day nothing happened — zero, not missing.
    const cash = future
      ? null
      : (cashByDate.get(date) ?? { date, billed: 0, moneyIn: 0, materials: 0, profit: 0 });
    return { number: i + 1, date, future, work, cash };
  });

  const billedBars: ChartBar[] = days.map((entry) => ({
    id: entry.date,
    label: String(entry.number),
    value: entry.cash === null ? null : entry.cash.moneyIn,
    ghost: entry.cash === null ? null : entry.cash.billed,
    selected: entry.date === day,
    title:
      entry.cash === null
        ? `${String(entry.number)} ${monthShort(month)}: not yet`
        : `${weekdayShort(entry.date)} ${String(entry.number)} ${monthShort(month)}: billed ${money(entry.cash.billed, currency)}, collected ${money(entry.cash.moneyIn, currency)}`,
  }));

  const workBars: ChartBar[] = days.map((entry) => {
    const booked = entry.work?.booked ?? 0;
    const finished = entry.work?.finished ?? 0;
    const ahead = entry.future && entry.work === null;
    return {
      id: entry.date,
      label: String(entry.number),
      // A day ahead has nothing finished yet; it shows its bookings alone.
      value: entry.future && finished === 0 ? null : finished,
      ghost: ahead ? null : booked,
      selected: entry.date === day,
      title: ahead
        ? `${String(entry.number)} ${monthShort(month)}: nothing booked yet`
        : `${weekdayShort(entry.date)} ${String(entry.number)} ${monthShort(month)}: expected ${money(booked, currency)} (${plural(entry.work?.bookedCount ?? 0, "booking")}), realized ${money(finished, currency)} (${String(entry.work?.finishedCount ?? 0)} finished)`,
    };
  });

  const cashBars: ChartBar[] = days.map((entry) => ({
    id: entry.date,
    label: String(entry.number),
    value: entry.cash === null ? null : entry.cash.profit,
    selected: entry.date === day,
    title:
      entry.cash === null
        ? `${String(entry.number)} ${monthShort(month)}: not yet`
        : `${weekdayShort(entry.date)} ${String(entry.number)} ${monthShort(month)}: in ${money(entry.cash.moneyIn, currency)}${hasSupply ? `, materials ${money(entry.cash.materials, currency)}` : ""}, profit ${money(entry.cash.profit, currency)}`,
  }));

  const counted = days.flatMap((entry) => (entry.cash === null ? [] : [entry.cash]));
  const dayTotal = counted.reduce(
    (sum, row) => ({
      moneyIn: sum.moneyIn + row.moneyIn,
      materials: sum.materials + row.materials,
      profit: sum.profit + row.profit,
    }),
    { moneyIn: 0, materials: 0, profit: 0 },
  );
  const booked = days.flatMap((entry) => (entry.work === null ? [] : [entry.work]));
  const picked = days.find((entry) => entry.date === day) ?? null;
  const pick = (id: string): void => setDay(id === day ? null : id);
  const pickedName =
    picked === null
      ? ""
      : `${weekdayShort(picked.date)} ${String(picked.number)} ${monthName(month)}`;

  return (
    <>
      {controls}

      <HeadlineTiles
        totals={total}
        owed={data.owed}
        started={monthRow.started}
        currency={currency}
        targets={targets}
      />

      <FigureTiles
        period={`${title}${monthRow.current ? " so far" : ""}`}
        totals={total}
        profitStarted={monthRow.started}
        currency={currency}
      />

      <section className={styles.card} data-testid="accounting-billed-by-day">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Billed and collected, {title}</h2>
          <span className={styles.spacer} />
          <BilledLegend />
        </div>
        <div className={styles.cardBody}>
          <ProfitChart
            bars={billedBars}
            currency={currency}
            label={`Billed and collected by day, ${title}`}
            onPick={(id) => {
              if (id > data.today) return;
              pick(id);
            }}
            height={160}
            showValues={false}
            labelSize={10}
            testId="accounting-billed-day-chart"
          />
          <p className={styles.note}>
            Billed is invoices issued that day; collected is payments received that day.
          </p>
        </div>
      </section>

      <div className={styles.pairGrid}>
        <section className={styles.card} data-testid="accounting-overhead-card">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Where the money goes</h2>
            <span className={styles.spacer} />
            <OverheadLegend />
          </div>
          <div className={styles.cardBody}>
            <OverheadRows groups={total.overhead} moneyIn={total.moneyIn} currency={currency} />
          </div>
        </section>

        <section className={styles.card} data-testid="accounting-owed-card">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Owed by age</h2>
          </div>
          <div className={styles.cardBody}>
            <OwedByAge owed={data.owed} currency={currency} />
          </div>
        </section>
      </div>

      <section className={styles.card} data-testid="accounting-work-by-day">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Expected and realized, {title}</h2>
          <span className={styles.spacer} />
          <WorkLegend />
        </div>
        <div className={styles.cardBody}>
          <ProfitChart
            bars={workBars}
            currency={currency}
            label={`Expected and realized revenue by day, ${title}`}
            onPick={pick}
            height={170}
            showValues={false}
            labelSize={10}
            testId="accounting-work-day-chart"
          />
          {picked !== null ? (
            <div className={styles.dayDetail} data-testid="accounting-work-day-detail">
              <span className={styles.strong}>{pickedName}</span>
              <span>
                Expected {money(picked.work?.booked ?? 0, currency)} ·{" "}
                {plural(picked.work?.bookedCount ?? 0, "booking")}
              </span>
              <span>
                Realized {money(picked.work?.finished ?? 0, currency)} ·{" "}
                {picked.work?.finishedCount ?? 0} finished
              </span>
            </div>
          ) : (
            <p className={styles.note}>
              Click a day for its numbers. Days ahead show what is booked.
            </p>
          )}
          <UnpricedNote count={total.unpriced} />
          <button
            type="button"
            className={`${styles.quiet} ${styles.toggle}`}
            onClick={() => setShowWork((was) => !was)}
            aria-expanded={showWork}
            data-testid="accounting-work-days-toggle"
          >
            {showWork ? "Hide the numbers" : "Show the numbers"}
          </button>
          {showWork &&
            (booked.length === 0 ? (
              <p className={styles.note}>No bookings this month.</p>
            ) : (
              <PagedRows rows={booked} per={10} noun="days">
                {(shown) => (
                  <div className={styles.tableWrap}>
                    <table className={styles.table} data-testid="accounting-work-day-table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th className={styles.right}>Bookings</th>
                          <th className={styles.right}>Expected</th>
                          <th className={styles.right}>Finished</th>
                          <th className={styles.right}>Realized</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((row) => (
                          <tr
                            key={row.date}
                            className={row.date === day ? styles.selectedRow : undefined}
                          >
                            <td>
                              {weekdayShort(row.date)} {parseDay(row.date).day}
                            </td>
                            <td className={styles.right}>{row.bookedCount}</td>
                            <td className={`${styles.right} ${signClass(row.booked) ?? ""}`}>
                              {money(row.booked, currency)}
                            </td>
                            <td className={styles.right}>{row.finishedCount}</td>
                            <td className={`${styles.right} ${signClass(row.finished) ?? ""}`}>
                              {money(row.finished, currency)}
                            </td>
                          </tr>
                        ))}
                        <tr className={styles.totalRow}>
                          <td>{monthName(month)}</td>
                          <td className={styles.right}>{total.booked.count}</td>
                          <td className={styles.right}>
                            {money(total.booked.profitCents, currency)}
                          </td>
                          <td className={styles.right}>{total.finished.count}</td>
                          <td className={styles.right}>
                            {money(total.finished.profitCents, currency)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </PagedRows>
            ))}
        </div>
      </section>

      <section className={styles.card} data-testid="accounting-flow">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Money in to profit, {title}</h2>
        </div>
        <div className={styles.cardBody}>
          <FlowChart
            flow={flow}
            currency={currency}
            label={`Where the money went, ${title}: locations into money in, then staff, supplies and lab, occupancy, other and profit`}
            testId="accounting-flow-chart"
          />
          <p className={styles.note}>
            Each block is sized by its money. A month that costs more than it took in shows a
            shortfall instead of profit.
          </p>
        </div>
      </section>

      <div className={styles.monthGrid}>
        <section className={styles.card} data-testid="accounting-by-day">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Cash profit by day</h2>
            <span className={styles.spacer} />
            <p className={styles.note}>Days leave out salaries and expenses</p>
          </div>
          <div className={styles.cardBody}>
            <ProfitChart
              bars={cashBars}
              currency={currency}
              label={`Profit by day, ${title}`}
              onPick={(id) => {
                if (id > data.today) return;
                pick(id);
              }}
              height={160}
              showValues={false}
              labelSize={10}
              testId="accounting-day-chart"
            />
            {picked?.cash != null ? (
              <div className={styles.dayDetail} data-testid="accounting-day-detail">
                <span className={styles.strong}>{pickedName}</span>
                <span>Money in {money(picked.cash.moneyIn, currency)}</span>
                {hasSupply && <span>Materials {money(picked.cash.materials, currency)}</span>}
                <span className={signClass(picked.cash.profit)}>
                  Profit {money(picked.cash.profit, currency)}
                </span>
              </div>
            ) : null}
            {monthRow.started ? (
              <p className={styles.note} data-testid="accounting-day-note">
                {hasSupply
                  ? "Daily profit is payments received minus materials received that day."
                  : "Daily profit is payments received that day."}{" "}
                The days add up to{" "}
                <b className={`${styles.strong} ${styles.num}`}>
                  {money(dayTotal.profit, currency)}
                </b>
                ; the month&rsquo;s profit is{" "}
                <b className={`${styles.strong} ${styles.num}`}>
                  {money(monthRow.profit, currency)}
                </b>{" "}
                once salaries ({money(monthRow.salaries, currency)}) and expenses (
                {money(monthRow.expenses, currency)}) come off.
              </p>
            ) : (
              <p className={styles.note}>{title} has not started.</p>
            )}
            <button
              type="button"
              className={`${styles.quiet} ${styles.toggle}`}
              onClick={() => setShowDays((was) => !was)}
              aria-expanded={showDays}
              data-testid="accounting-days-toggle"
            >
              {showDays ? "Hide the numbers" : "Show the numbers"}
            </button>
            {showDays && (
              <PagedRows rows={counted} per={10} noun="days">
                {(shown) => (
                  <div className={styles.tableWrap}>
                    <table className={styles.table} data-testid="accounting-day-table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th className={styles.right}>Money in</th>
                          {hasSupply && <th className={styles.right}>Materials</th>}
                          <th className={styles.right}>Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((row) => (
                          <tr
                            key={row.date}
                            className={row.date === day ? styles.selectedRow : undefined}
                          >
                            <td>
                              {weekdayShort(row.date)} {parseDay(row.date).day}
                            </td>
                            <td className={styles.right}>{money(row.moneyIn, currency)}</td>
                            {hasSupply && (
                              <td className={styles.right}>{money(row.materials, currency)}</td>
                            )}
                            <td className={`${styles.right} ${signClass(row.profit) ?? ""}`}>
                              {money(row.profit, currency)}
                            </td>
                          </tr>
                        ))}
                        <tr className={styles.totalRow}>
                          <td>Days total</td>
                          <td className={styles.right}>{money(dayTotal.moneyIn, currency)}</td>
                          {hasSupply && (
                            <td className={styles.right}>{money(dayTotal.materials, currency)}</td>
                          )}
                          <td className={`${styles.right} ${signClass(dayTotal.profit) ?? ""}`}>
                            {money(dayTotal.profit, currency)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </PagedRows>
            )}
          </div>
        </section>

        <section className={styles.card} data-testid="accounting-by-category">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>By procedure category</h2>
            <span className={styles.spacer} />
            <WorkLegend />
          </div>
          <div className={styles.cardBody}>
            <CategoryBars
              rows={monthRow.byProcedureCategory}
              categories={data.procedureCategories}
              currency={currency}
            />
            <p className={styles.note}>Realized of expected, by the category of each procedure.</p>
          </div>
        </section>
      </div>
    </>
  );
}
